<template>
  <div class="modal-factory">
    <v-dialog
      v-if="stack.length"
      :model-value="activeDialog.open"
      v-bind="activeDialog.props"
      @update:model-value="onDialogInput($event)"
    >
      <component
        :is="activeDialog.component"
        v-bind="activeDialog.payload"
        @return="onDialogReturnValue"
        @close="onDialogInput(false)"
      />
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { sleep } from '@/utils/helpers'

// NOTE dialogs could have #dialog=name urls associated with them if wanted
// would just have to set/unset them on input change

// dialogs render as a stack, always showing the latest addition traveling backwards

const stack = ref([])

const activeDialog = computed(() => {
  return stack.value[stack.value.length - 1]
})

// Expose the factory methods to app via globalProperties
onMounted(() => {
  const instance = getCurrentInstance()
  if (instance && instance.appContext.config.globalProperties) {
    instance.appContext.config.globalProperties.$modal = {
      open,
      onDialogInput,
      onDialogReturnValue
    }
  }
})

async function open(name, opts = {}) {
  if (!name && !opts.component) throw new Error('No dialog component resolvable')

  const module = await import(`@/dialogs/${name}.vue`)
  const component = module.default


  // Access dialogProps from the component definition
  const dialogProps = component.dialogProps || {}

  const props = {
    ...dialogProps,
    ...opts.props,
  }


  const dialog = {
    id: Date.now().toString(36) + Math.random().toString(36).substr(2),
    open: true,
    component,
    props,
    payload: opts.payload,
    remove() {
      this.open = false

      // remove from stack if not current dialog
      if (activeDialog.value?.id !== this.id) {
        stack.value = stack.value.filter(i => i.id !== this.id)
      }
    },
  }

  // if dialog active, transition to new dialog first
  // NOTE this will wipe any component state in old dialog
  // to get around this, we could v-for all dialogs and only show dialog with activeDialog id
  if (stack.value.length) {
    const currDialog = activeDialog.value
    currDialog.open = false
    sleep(300)
      .then(() => stack.value.push(dialog))
      .then(() => { currDialog.open = true })
  } else {
    stack.value.push(dialog)
  }

  if (props.returnValue) {
    dialog.result = new Promise((res, rej) => {
      dialog.resolve = res
      dialog.reject = rej
    })
  }

  return dialog
}

function onDialogInput(value) {
  if (value) return // dont handle opens
  const dialog = activeDialog.value

  // resolve returnValue promise with null
  if (dialog.result) dialog.resolve(null)

  dialog.open = false
  sleep(300).then(() => stack.value.pop())
}

function onDialogReturnValue({ data, reject }) {
  console.log('OnDialogReturnValue', data, reject)
  if (activeDialog.value.result) {
    if (reject) activeDialog.value.reject(data)
    else activeDialog.value.resolve(data)
  }
}
</script>
